import React, { Component } from 'react';
import { win } from '@wix/cashier-common/dist/src/utils/win';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { type ITokenizeCardToken } from '@wix/cashier-core/dist/src/services/CreditCard/types';
import { dh } from '@wix/payments-test-utils';
import { defaultTheme } from '../../contexts/Theme/constants';
import { CardFormProviders } from '../CardFormProviders';
import { square } from '../../providers/square';
import { initSquareAPI } from '../../providers/square/initSquareAPI';
import { tokenizeViaSquare } from '../../providers/square/tokenizeViaSquare';
import { tokenizeVia2Checkout } from '../../providers/two-checkout/tokenizeVia2Checkout';
import { tokenizeViaStripe } from '../../providers/stripe/tokenizeViaStripe';
import '../../styles/globals.scss';
import { type BillingInfo } from '../../types/BillingInfo';
import { type FieldName } from '../../types/CardFieldName';
import { type CardPublicData } from '../../types/CardPublicData';
import { type BaseConfig, type Config } from '../../types/Config';
import { ErrorMessage } from '../../types/ErrorMessage';
import { type Expiration } from '../../types/Expiration';
import { InboundMessageType } from '../../types/InboundMessageType';
import { InteractionName } from '../../types/InteractionName';
import { OutboundEventName } from '../../types/OutboundEventName';
import { OutboundMessageType } from '../../types/OutboundMessageType';
import { type TokenizeResponse } from '../../types/TokenizeResponse';
import { type Translations } from '../../types/Translations';
import { type ValidateResult } from '../../types/ValidateResult';
import { initI18nAndBi } from '../../utils/initI18nAndBi';
import { DH } from '../../utils/DataHook';
import { getDeviceType } from '../../utils/deviceType';
import { captureIfThrows } from '../../utils/errorReporter';
import { panoramaClient } from '../../utils/panoramaClient/panoramaClient';
import { filterDefined } from '../../utils/filterDefined';
import { generateFieldMask } from '../../utils/generateFieldMask';
import { getRequiredFields } from '../../utils/getRequiredFields';
import { emitMessage } from '../../utils/messages/emitMessage';
import { replyToMessage } from '../../utils/messages/replyToMessage';
import { sendEventMessage } from '../../utils/messages/sendEventMessage';
import { isSaveCcDisabled } from '../../utils/isSaveCcDisabled';
import { type InboundEmitMessage, type MessageResponse } from '../../utils/messages/types';
import { cardAPI } from '../../utils/serverAPI/cardAPI';
import { isFrameVisible, waitForFrameVisible } from '../../utils/waitForFrameVisible';
import { tokenizeTestCard } from '../../providers/test/tokenizeTestCard';
import { CardForm } from '../CardForm';
import { type CardFormAPI, type CardRequestData, type SavedCardRequestData } from '../CardForm/types';
import { transformExpireStrToObj } from '../CardForm/utils/formatting';
import { messenger, type UnListenMessage } from '../../utils/messenger';
import { withStyles } from '../../utils/withStyles';
import { type SqVerificationDetails } from '../../providers/square/types';
import {
  type CompleteMessageHandlersMap,
  createFocusInvalidFieldHandler,
  createValidateHandler,
  processInboundMessage,
} from '../CardFormPublic/utils/processInboundMessage';

export interface AppProps extends BaseConfig, Config {
  l10n: Translations;
}

interface AppComponentState {
  requiredFields: FieldName[];
  isStylesLoaded: boolean;
  isFormReady: boolean;
}

class AppClass extends Component<AppProps, AppComponentState> {
  static defaultProps = {
    theme: defaultTheme,
  };
  cardFormAPI: CardFormAPI = null;
  resizeObserver: ResizeObserver;
  isComponentMounted = false;
  private unlistenPostMessage: UnListenMessage;

  constructor(props: AppProps) {
    super(props);

    this.state = {
      requiredFields: getRequiredFields({
        providerId: props.providerId,
        savedCard: props.savedCard,
      }),
      isStylesLoaded: true, // site colors/fonts/etc
      isFormReady: this.props.providerId !== PaymentProvider.Square, // Square form initialized asynchronously
    };

    initI18nAndBi({
      locale: props.locale,
      l10n: props.l10n,
      appId: props.appId,
      appInstanceId: props.appInstanceId,
      orderId: props.orderId,
      biData: props.biData,
      visitorId: props.visitorId,
      msid: props.msid,
      checkoutSessionId: props.checkoutSessionId,
      appSessionId: props.appSessionId,
      pageType: props.pageType,
    });
    this.setStyleAPIVariables(this.props.styleApiCssVariables);
  }

  handlePostMessage = captureIfThrows(async (event: { data: InboundEmitMessage } & MessageEvent) => {
    if (
      event.source !== win.parent ||
      event.origin !== this.props.parentOrigin ||
      !event.data ||
      !event.data.messageId
    ) {
      return;
    }

    const payloadData: { [key: string]: any } = event.data.payload?.data || {};
    const messageType: InboundMessageType = event.data.payload?.type;
    if (!messageType) {
      return;
    }

    const handlers: CompleteMessageHandlersMap = {
      [InboundMessageType.Validate]: createValidateHandler(this.cardFormAPI),
      [InboundMessageType.GetPublicData]: () => this.getPublicData(),
      [InboundMessageType.Tokenize]: () => this.tokenize(payloadData),
      [InboundMessageType.FocusInvalidField]: createFocusInvalidFieldHandler(this.cardFormAPI, payloadData),
      [InboundMessageType.ForceShowSavedCardCVV]: () => this.forceShowSavedCardCVV(),
      [InboundMessageType.SetStyleAPIVariables]: () => this.setStyleAPIVariables(payloadData),
    };

    const result = await processInboundMessage(messageType, this.cardFormAPI, handlers);

    replyToMessage(event, result);
  }, `Failed to handle inbound post message.`);

  forceShowSavedCardCVV(): MessageResponse | undefined {
    if (!this.props.savedCard || this.props.savedCard.withCVV) {
      return {
        error: {
          message: ErrorMessage.UnavailableOperation,
        },
      };
    }

    this.setState({
      requiredFields: getRequiredFields({
        providerId: this.props.providerId,
        savedCard: {
          ...this.props.savedCard,
          withCVV: true,
        },
      }),
    });

    return undefined;
  }

  getPublicData(): MessageResponse<CardPublicData> {
    if (this.props.savedCard) {
      const savedCardData = this.cardFormAPI.getSavedCardData();

      if (savedCardData.expiration) {
        return {
          expiration: transformExpireStrToObj(savedCardData.expiration),
        };
      }

      return {};
    }

    if (this.props.providerId === PaymentProvider.Square) {
      if (!square.api.getPublicData()) {
        return {
          error: {
            message: ErrorMessage.UnavailableBeforeTokenize,
          },
        };
      }

      return square.api.getPublicData();
    }

    const cardData = this.cardFormAPI.getCardData();
    const expiration: Expiration = transformExpireStrToObj(cardData.expiration);

    return {
      firstSixDigits: cardData.number.slice(0, 6),
      lastFourDigits: cardData.number.slice(-4),
      type: cardData.network,
      expiration,
      holderName: cardData.holderName,
    };
  }

  formatCardData(billingInfo: BillingInfo = {}): CardRequestData {
    const { number, cvv, expiration, holderName } = this.cardFormAPI.getCardData();
    const { holderId, ...billingAddress } = billingInfo;

    return {
      cvv,
      creditCard: {
        number,
        expiration: transformExpireStrToObj(expiration),
        additionalInformation: {
          holderName,
          ...(holderId !== undefined && { holderId }),
        },
        billingAddress,
      },
    };
  }

  formatSavedCardData(billingInfo: BillingInfo = {}): SavedCardRequestData {
    const { savedCard } = this.props;
    const { cvv } = this.cardFormAPI.getSavedCardData();

    const { holderId, ...billingAddress } = billingInfo;

    const requestData: SavedCardRequestData = {
      creditCard: {
        billingAddress,
      },
      fieldMask: generateFieldMask(billingAddress, 'creditCard.billingAddress'),
    };

    if (savedCard.withCVV) {
      requestData.cvv = cvv;
    }

    return requestData;
  }

  async tokenize({
    appInstance,
    billingInfo,
    sqVerificationDetails,
  }: {
    appInstance?: string;
    billingInfo?: BillingInfo;
    sqVerificationDetails?: SqVerificationDetails;
  } = {}): Promise<MessageResponse<TokenizeResponse>> {
    const { isTestPayment, savedCard, providerId } = this.props;
    if (savedCard && isSaveCcDisabled(providerId)) {
      return {
        error: {
          message: ErrorMessage.UnavailableForProvider,
        },
      };
    }

    // Test payments don't support saved cards; the saved-card form renders no number field,
    // so test tokenization has no card data to read.
    if (isTestPayment && savedCard) {
      return {
        error: {
          message: ErrorMessage.UnavailableOperation,
        },
      };
    }

    if (isTestPayment) {
      return tokenizeTestCard(this.cardFormAPI.getCardData());
    }

    if (providerId === PaymentProvider.Square) {
      return tokenizeViaSquare(sqVerificationDetails);
    }

    if (providerId === PaymentProvider.TwoCheckout) {
      panoramaClient.transaction(InteractionName.TWO_CHECKOUT_SDK_TOKENIZE).start();
      const { sellerId, publishableKey } = this.props;
      const { number, expiration, cvv, holderName } = this.cardFormAPI.getCardData();
      const expireObj: Expiration = transformExpireStrToObj(expiration);

      let numberToken: string;
      try {
        numberToken = await tokenizeVia2Checkout({
          sellerId,
          publishableKey,
          card: {
            number,
            cvv,
            expire: expireObj,
          },
        });
      } catch (error) {
        return { error };
      }
      panoramaClient.transaction(InteractionName.TWO_CHECKOUT_SDK_TOKENIZE).finish();

      return {
        numberToken,
        holderName,
        expiration: expireObj,
      };
    }

    if (providerId === PaymentProvider.StripeGateway) {
      panoramaClient.transaction(InteractionName.STRIPE_SDK_TOKENIZE).start();
      const { publishableKey } = this.props;
      const { number, expiration, cvv, holderName } = this.cardFormAPI.getCardData();
      const expireObj: Expiration = transformExpireStrToObj(expiration);
      let numberToken: string;
      try {
        numberToken = await tokenizeViaStripe({
          publishableKey,
          billingInfo,
          card: {
            number,
            cvv,
            expire: expireObj,
          },
        });
      } catch (error) {
        return { error };
      }
      panoramaClient.transaction(InteractionName.STRIPE_SDK_TOKENIZE).finish();

      return {
        numberToken,
        holderName,
        expiration: expireObj,
      };
    }

    const serviceConfig: Parameters<typeof cardAPI.tokenizeCard>[1] = {
      baseURLForPCI: this.props.apiBaseURLOverride,
      appInstance,
      revalidateAppInstance: this.revalidateAppInstance,
      ...(this.props.wixHeaders && {
        additionalHeaders: this.props.wixHeaders,
      }),
    };

    const tokenizeResult: ITokenizeCardToken = savedCard
      ? await cardAPI.tokenizeSavedCard(savedCard.id, this.formatSavedCardData(billingInfo), serviceConfig)
      : await cardAPI.tokenizeCard(this.formatCardData(billingInfo), serviceConfig);

    return filterDefined({
      numberToken: tokenizeResult.token,
      securityCodeToken: tokenizeResult.securityCodeToken,
      holderName: tokenizeResult.creditCard?.additionalInformation?.holderName,
      expiration: tokenizeResult.creditCard?.expiration,
    });
  }

  revalidateAppInstance = () =>
    emitMessage(
      {
        type: OutboundMessageType.GetNewAppInstance,
      },
      win.parent,
      this.props.parentOrigin,
    );

  sendEventMessage = captureIfThrows((eventName: OutboundEventName, payload: any) => {
    return sendEventMessage(eventName, payload, this.props.parentOrigin);
  });

  handleFormChange = (validityState: ValidateResult) => {
    this.sendEventMessage(OutboundEventName.Change, validityState);
  };

  getAppSize = (): {
    width: number;
    height: number;
  } => {
    const rootEl: HTMLElement = document.querySelector(dh(DH.Form));
    const size = rootEl.getBoundingClientRect();

    return {
      width: size.width,
      height: size.height,
    };
  };

  sendSizeChangeEvent = () => {
    this.sendEventMessage(OutboundEventName.SizeChange, this.getAppSize());
  };

  handleDeviceTypeChange = captureIfThrows(async () => {
    if (
      this.props.providerId === PaymentProvider.Square &&
      !this.props.deviceType // if deviceType not provided we rely on media query
    ) {
      square.api.changeDeviceType(getDeviceType());
    }
  }, `Failed to handle device type change.`);

  handleFormReady = () => {
    this.sendEventMessage(OutboundEventName.Ready, {
      sizes: this.getAppSize(),
    });
  };

  handleSquareInitChange = (isInited: boolean) => {
    if (this.isComponentMounted) {
      this.setState({ isFormReady: isInited });
    }
  };

  async initSquare() {
    const { theme } = this.props;

    const initSquareApi = () =>
      initSquareAPI(
        {
          locationId: this.props.squareLocationId,
          applicationId: this.props.squareAppId,
          deviceType: this.props.deviceType || getDeviceType(),
          theme,
        },
        (error) => {
          const message = error instanceof Error ? error.message : String(error);
          this.sendEventMessage(OutboundEventName.Crash, `Failed to init Square API. ${message}`);
        },
      );

    square.api.listenInitChange(this.handleSquareInitChange);

    if (isFrameVisible()) {
      await initSquareApi();
      this.handleFormReady();
    } else {
      // if frame is invisible (e.g. in PayApp or Stores Checkout) initing Square will produce warnings
      // (and potentially break square form), thus we need to wait for visibility
      // but holding formReady handler (message to parent frame & emitting monitoring events) is not an option
      // due to 1. public component will throw timeout (to onLoadFailed) and 2. monitoring events will hang
      // (or will produce very high duration)
      // thus we act as if form ready, but actual isFormReady toggle will be made only after Square init
      this.handleFormReady();
      await waitForFrameVisible();
      await initSquareApi();
    }
  }

  setStyleAPIVariables = async (cssVars: { [cssVar: string]: string }) => {
    if (!cssVars) {
      return;
    }
    const root = document.documentElement;

    for (const [cssVar, value] of Object.entries(cssVars)) {
      value ? root.style.setProperty(cssVar, value) : root.style.removeProperty(cssVar);
    }
  };

  componentDidMount() {
    this.isComponentMounted = true;
    this.unlistenPostMessage = messenger.addListener(this.handlePostMessage, `inside-the-iframe`);

    try {
      this.resizeObserver = new win.ResizeObserver(this.sendSizeChangeEvent);
      this.resizeObserver.observe(document.body);
    } catch (error) {
      console.error(
        `ResizeObserver failed. Dynamic size updates won't be sent to Widget, resulting in scroll in card form iframe`,
        error,
      );
    }

    if (this.props.providerId === PaymentProvider.Square) {
      // the "catch" needed for to allow writing failure test in Jest
      this.initSquare().catch(() => null);
    } else {
      this.handleFormReady();
    }
  }

  componentWillUnmount() {
    this.isComponentMounted = false;

    this.unlistenPostMessage();
    this.cardFormAPI = null;

    if (this.resizeObserver) {
      this.resizeObserver.unobserve(document.body);
      this.resizeObserver = null;
    }

    if (this.props.providerId === PaymentProvider.Square) {
      square.api.unlistenInitChange(this.handleSquareInitChange);
    }
  }

  render() {
    return (
      <CardFormProviders
        locale={this.props.locale}
        country={this.props.country}
        theme={this.props.theme}
        providerId={this.props.providerId}
        deviceType={this.props.deviceType}
        onDeviceTypeChange={this.handleDeviceTypeChange}
        isHidden={!this.state.isFormReady || !this.state.isStylesLoaded}
        isSavedCard={!!this.props.savedCard}
        appId={this.props.appId}
        labelsStyle={this.props.labelsStyle}
        omitOffset={this.props.omitOffset}
        isFullWidth={this.props.isFullWidth}
      >
        <CardForm
          fields={this.state.requiredFields}
          savedCardNetwork={this.props.savedCard?.network}
          savedCardExpiration={this.props.savedCard?.expiration}
          onApiInit={(cardFormAPI) => (this.cardFormAPI = cardFormAPI)}
          onChange={this.handleFormChange}
        />
      </CardFormProviders>
    );
  }
}

export const App = withStyles(AppClass);
